import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, X } from 'lucide-react';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';
import { FLOATING_KEYBOARD_ANIM_MS } from '../../utils/learnerSlideModalMotion';

const PANEL_ANIM_MS = FLOATING_KEYBOARD_ANIM_MS;
const GAP_ABOVE_ANCHOR_PX = 8;

export interface TagSourceLessonOption {
  lessonId: number;
  lessonTitle: string;
  courseTitle: string;
  levelTitle: string;
}

interface TagSourceLessonChoiceProps {
  open: boolean;
  isRtl: boolean;
  lessons: TagSourceLessonOption[];
  anchorRef?: React.RefObject<HTMLElement | null>;
  onDismiss: () => void;
  onChoose: (lessonId: number) => void;
}

/**
 * Compact slide-in chooser, same shell as the "lesson not passed yet" hint.
 */
export const TagSourceLessonChoice: React.FC<TagSourceLessonChoiceProps> = ({
  open,
  isRtl,
  lessons,
  anchorRef,
  onDismiss,
  onChoose
}) => {
  const { t } = useTranslation('learning');
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; right: number; width: number } | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLessonIdRef = useRef<number | null>(null);

  const updateAnchorRect = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) {
      setAnchorRect(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setAnchorRect({ top: rect.top, left: rect.left, right: rect.right, width: rect.width });
  }, [anchorRef]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
      pendingLessonIdRef.current = null;
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !mounted) return;
    updateAnchorRect();
    const raf = window.requestAnimationFrame(updateAnchorRect);
    window.addEventListener('resize', updateAnchorRect);
    window.addEventListener('scroll', updateAnchorRect, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateAnchorRect);
      window.removeEventListener('scroll', updateAnchorRect, true);
    };
  }, [open, mounted, updateAnchorRect]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  const finishExit = useCallback(() => {
    const lessonId = pendingLessonIdRef.current;
    pendingLessonIdRef.current = null;
    setMounted(false);
    setExiting(false);
    if (lessonId != null) onChoose(lessonId);
    else onDismiss();
  }, [onChoose, onDismiss]);

  const startExit = useCallback(
    (lessonId: number | null) => {
      if (exiting) return;
      pendingLessonIdRef.current = lessonId;
      if (prefersReducedMotion()) {
        finishExit();
        return;
      }
      setExiting(true);
      exitTimerRef.current = setTimeout(finishExit, PANEL_ANIM_MS);
    },
    [exiting, finishExit]
  );

  useHistoryBackClose(open, () => {
    startExit(null);
  }, 'tag-source-lesson-choice');

  if (!mounted) return null;

  const slideClass = exiting
    ? isRtl
      ? 'lesson-not-passed-hint-exit-rtl'
      : 'lesson-not-passed-hint-exit-ltr'
    : isRtl
      ? 'lesson-not-passed-hint-enter-rtl'
      : 'lesson-not-passed-hint-enter-ltr';

  const viewportMargin = 12;
  const panelWidth = anchorRect
    ? Math.min(352, Math.max(anchorRect.width, 260), window.innerWidth - viewportMargin * 2)
    : 0;
  const anchoredLeft = anchorRect
    ? Math.min(
        Math.max(isRtl ? anchorRect.left : anchorRect.right - panelWidth, viewportMargin),
        window.innerWidth - panelWidth - viewportMargin
      )
    : 0;
  const anchoredStyle: React.CSSProperties | undefined = anchorRect
    ? {
        position: 'fixed',
        top: anchorRect.top - GAP_ABOVE_ANCHOR_PX,
        left: anchoredLeft,
        width: panelWidth,
        maxWidth: `calc(100vw - ${viewportMargin * 2}px)`,
        transform: 'translateY(-100%)',
        zIndex: 110
      }
    : undefined;

  return (
    <div
      className={
        anchoredStyle
          ? 'pointer-events-none'
          : `pointer-events-none fixed top-[4.5rem] z-[110] w-[min(100vw-1.5rem,22rem)] sm:top-20 ${
              isRtl ? 'start-3 sm:start-4' : 'end-3 sm:end-4'
            }`
      }
      style={anchoredStyle}
      role="presentation"
    >
      <div
        className={`lesson-not-passed-hint-panel pointer-events-auto overflow-hidden rounded-xl border border-amber-200/80 bg-white/95 shadow-lg shadow-amber-900/10 backdrop-blur-sm dark:border-amber-800/45 dark:bg-gray-900/95 dark:shadow-black/30 ${slideClass}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tag-source-lesson-choice-title"
      >
        <div className="relative px-3.5 pb-3 pt-3 sm:px-4 sm:pb-3.5 sm:pt-3.5">
          <button
            type="button"
            onClick={() => startExit(null)}
            className="absolute end-2 top-2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label={t('tagSourceLesson.close', { defaultValue: 'Close' })}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
          <div className="flex gap-2.5 pe-6">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-200">
              <BookOpen className="h-4 w-4" strokeWidth={2.25} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p id="tag-source-lesson-choice-title" className="text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50">
                {t('tagSourceLesson.title', { defaultValue: 'Which lesson do you want to open?' })}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                {t('tagSourceLesson.body', {
                  defaultValue: 'This part of the tag includes content from more than one lesson.'
                })}
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-1.5">
            {lessons.map((lesson) => (
              <button
                key={lesson.lessonId}
                type="button"
                onClick={() => startExit(lesson.lessonId)}
                className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-start text-xs font-medium text-indigo-900 transition-colors hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-950/40 dark:text-indigo-100 dark:hover:bg-indigo-950/60"
              >
                <span className="block truncate">{lesson.lessonTitle}</span>
                <span className="mt-0.5 block truncate text-[11px] font-normal text-indigo-700/80 dark:text-indigo-200/80">
                  {lesson.courseTitle} · {lesson.levelTitle}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
